/**
 * Single access point to every browser-storage key that carries match data.
 *
 * Nothing outside this module may touch those keys - `matchStorage.test.ts`
 * fails the build if it happens. The rule exists because the keys used to be
 * read, written and purged from six different files, which is how a leftover
 * `pendingMatchData` ended up shadowing the parameters of an incoming launch
 * link (see CHANGELOG 1.5.1).
 *
 * Two storages, two lifetimes:
 *
 * - `localStorage` holds what must survive closing the app: the running match
 *   and the launch payload that lets it be rebuilt.
 * - `sessionStorage` holds what only makes sense in the current tab: the
 *   hand-off between a screen and the reload that follows it.
 *
 * Reading the URL is NOT this module's business. `useMatchData` owns that
 * policy: the URL wins, and only then does it fall back here.
 */

// ---------------------------------------------------------------------------
// Keys
// ---------------------------------------------------------------------------

/** Running match state (compressed), written by useMatchPersistence. */
const MATCH_STATE_KEY = 'currentMatchState';

/** Launch payload, persistent copy: survives closing the tab. */
const MATCH_INPUT_KEY = 'matchData';

/** Launch payload, tab-scoped copy: hand-off across a reload. */
const PENDING_INPUT_KEY = 'pendingMatchData';

/** Court configuration coming from a QR scan, tab-scoped. */
const PENDING_SETUP_KEY = 'pendingSetupData';

/**
 * Court this tablet is paired with (device token, court setup). Persistent and
 * deliberately NOT part of clearAll(): the pairing outlives every match.
 */
const PAIRED_COURT_KEY = 'pairedCourt';

/** Saved matches older than this are ignored and dropped on read. */
const MAX_AGE_MS = 24 * 60 * 60 * 1000;

// ---------------------------------------------------------------------------
// Schema migration
// ---------------------------------------------------------------------------

const SCHEMA_VERSION_KEY = 'storageSchemaVersion';
const SCHEMA_VERSION = 1;

/**
 * Keys this app used to write and no longer does. Devices that ran an older
 * version still carry them; `migrateStorage` drops them once, on the next
 * start, and records the schema version so it does not run again.
 *
 * Add to this list whenever a key is retired, and bump SCHEMA_VERSION. That is
 * the whole point of the mechanism: retiring a key has a place to be written
 * down, instead of a `removeItem` kept "for one version" that nobody removes.
 *
 * - `matchBackup`   : byte-identical copy of currentMatchState, never read back
 * - `lastMatchData` : launch payload written by useMatchData, never read back
 *                     (the fallback read `matchData`, which it never wrote)
 */
const RETIRED_LOCAL_KEYS = ['matchBackup', 'lastMatchData'];

/**
 * Drops keys retired by earlier versions. Safe to call on every start: it does
 * nothing once the recorded schema version is up to date.
 */
export function migrateStorage(): void {
  try {
    const recorded = Number(localStorage.getItem(SCHEMA_VERSION_KEY) ?? 0);
    if (recorded >= SCHEMA_VERSION) return;

    RETIRED_LOCAL_KEYS.forEach(key => localStorage.removeItem(key));
    localStorage.setItem(SCHEMA_VERSION_KEY, String(SCHEMA_VERSION));
  } catch {
    // Private mode, quota, disabled storage: migration is best-effort.
  }
}

// ---------------------------------------------------------------------------
// Launch payload
// ---------------------------------------------------------------------------

export type MatchInputSource = 'sessionStorage' | 'localStorage';

export interface MatchInputRead {
  data: any;
  source: MatchInputSource;
}

/**
 * Stores the launch payload in both lifetimes at once.
 *
 * Callers used to write one or the other, which is why a match launched from a
 * link had no persistent fallback while a manually created one did.
 */
export function saveMatchInput(data: any): void {
  try {
    const json = JSON.stringify(data);
    sessionStorage.setItem(PENDING_INPUT_KEY, json);
    localStorage.setItem(MATCH_INPUT_KEY, json);
  } catch (error) {
    console.warn('Storage: cannot save match input', error);
  }
}

/**
 * Reads the launch payload, tab-scoped copy first.
 *
 * Deliberately does NOT consume what it reads: the payload lives until
 * `clearAll()`. Consuming it used to lose the match on a second refresh taken
 * before the match was started.
 */
export function readMatchInput(): MatchInputRead | null {
  const attempts: Array<[Storage, string, MatchInputSource]> = [
    [sessionStorage, PENDING_INPUT_KEY, 'sessionStorage'],
    [localStorage, MATCH_INPUT_KEY, 'localStorage'],
  ];

  for (const [store, key, source] of attempts) {
    try {
      const raw = store.getItem(key);
      if (!raw) continue;
      return { data: JSON.parse(raw), source };
    } catch (error) {
      console.warn(`Storage: invalid ${key}, dropping it`, error);
      try {
        store.removeItem(key);
      } catch {
        // ignore
      }
    }
  }

  return null;
}

// ---------------------------------------------------------------------------
// QR court configuration
// ---------------------------------------------------------------------------

export function saveSetupInput(setup: any): void {
  try {
    sessionStorage.setItem(PENDING_SETUP_KEY, JSON.stringify(setup));
  } catch (error) {
    console.warn('Storage: cannot save setup input', error);
  }
}

export function readSetupInput(): any | null {
  try {
    const raw = sessionStorage.getItem(PENDING_SETUP_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch (error) {
    console.warn('Storage: invalid pendingSetupData, dropping it', error);
    clearSetupInput();
    return null;
  }
}

export function clearSetupInput(): void {
  try {
    sessionStorage.removeItem(PENDING_SETUP_KEY);
  } catch {
    // ignore
  }
}

// ---------------------------------------------------------------------------
// Paired court (referee tablet bound to a court)
// ---------------------------------------------------------------------------

export function savePairedCourt(paired: any): void {
  try {
    localStorage.setItem(PAIRED_COURT_KEY, JSON.stringify(paired));
  } catch (error) {
    console.warn('Storage: cannot save paired court', error);
  }
}

export function readPairedCourt(): any | null {
  try {
    const raw = localStorage.getItem(PAIRED_COURT_KEY);
    if (!raw) return null;
    const paired = JSON.parse(raw);
    // A record without its token or court is useless: drop it rather than poll with it
    if (!paired || typeof paired.deviceToken !== 'string' || !paired.court) {
      clearPairedCourt();
      return null;
    }
    return paired;
  } catch (error) {
    console.warn('Storage: invalid pairedCourt, dropping it', error);
    clearPairedCourt();
    return null;
  }
}

export function clearPairedCourt(): void {
  try {
    localStorage.removeItem(PAIRED_COURT_KEY);
  } catch {
    // ignore
  }
}

// ---------------------------------------------------------------------------
// Running match state
// ---------------------------------------------------------------------------

export function saveMatchState(persisted: unknown): void {
  try {
    localStorage.setItem(MATCH_STATE_KEY, JSON.stringify(persisted));
  } catch (error) {
    console.warn('Storage: cannot save match state', error);
  }
}

/** Same as saveMatchState, for a payload already serialised by the caller. */
export function saveMatchStateRaw(json: string): void {
  try {
    localStorage.setItem(MATCH_STATE_KEY, json);
  } catch (error) {
    console.warn('Storage: cannot save match state', error);
  }
}

/**
 * Reads the saved match, or null when there is none, when it is unreadable, or
 * when it is older than 24 hours. Expired and corrupt entries are dropped.
 */
export function readMatchState(): any | null {
  try {
    const raw = localStorage.getItem(MATCH_STATE_KEY);
    if (!raw) return null;

    const data = JSON.parse(raw);
    if (Date.now() - data.timestamp < MAX_AGE_MS) return data;

    clearMatchState();
    return null;
  } catch (error) {
    console.warn('Storage: invalid saved match, dropping it', error);
    clearMatchState();
    return null;
  }
}

export function clearMatchState(): void {
  try {
    localStorage.removeItem(MATCH_STATE_KEY);
  } catch {
    // ignore
  }
}

// ---------------------------------------------------------------------------
// Purge
// ---------------------------------------------------------------------------

/**
 * Drops the launch payload only, leaving the running match alone.
 *
 * This is what a fresh QR scan needs: it supersedes a launch link waiting in
 * the session, but it must NOT decide the fate of a match in progress - that
 * is the recovery modal's job, and the referee's call. Using clearAll() here
 * silently destroyed a running match.
 */
export function clearMatchInput(): void {
  try {
    localStorage.removeItem(MATCH_INPUT_KEY);
    sessionStorage.removeItem(PENDING_INPUT_KEY);
  } catch (error) {
    console.warn('Storage: cannot clear match input', error);
  }
}

/**
 * Drops every match-related key: running match, launch payload, and the QR
 * court configuration. The paired court is NOT a match key and stays.
 *
 * The QR configuration is included on purpose. Leaving it behind meant that
 * after validating a match the app reopened on the previously scanned court,
 * a behaviour nothing asked for and nothing documented.
 *
 * This is the only purge in the app. It replaces five hand-written copies that
 * had drifted apart, one of which swept `localStorage` for any key containing
 * "Match" - which would silently have eaten any future key so named.
 */
export function clearAll(): void {
  try {
    localStorage.removeItem(MATCH_STATE_KEY);
    localStorage.removeItem(MATCH_INPUT_KEY);
    sessionStorage.removeItem(PENDING_INPUT_KEY);
    sessionStorage.removeItem(PENDING_SETUP_KEY);
  } catch (error) {
    console.warn('Storage: cannot clear match data', error);
  }
}

/** Exposed for the guard test, which checks no other file names these keys. */
export const MATCH_STORAGE_KEYS = {
  MATCH_STATE_KEY,
  MATCH_INPUT_KEY,
  PENDING_INPUT_KEY,
  PENDING_SETUP_KEY,
  PAIRED_COURT_KEY,
  SCHEMA_VERSION_KEY,
  RETIRED_LOCAL_KEYS,
} as const;
